import { CheckCircleOutline, Close, ErrorOutline, HourglassEmpty, InfoOutlined } from '@mui/icons-material';
import { Button, Card, CircularProgress, IconButton, LinearProgress, Snackbar, Stack, Typography } from '@mui/material';
import { isClosedJobStatus, JobResponse } from '@parkchirp/reporting';
import { useState } from 'react';

type Props = {
	job: JobResponse;
	reportTitle: string;
	isCancellingReport: boolean;
	onCancel: () => void;
	onDismiss: () => void;
	onDownload: (job: JobResponse) => void;
};

const getPanelContent = (job: JobResponse) => {
	switch (job.status) {
		case 'queued':
			return {
				icon: <HourglassEmpty color='action' />,
				label: 'Queued',
				description: 'Waiting for report generation to begin.',
				showProgress: true,
			};
		case 'running':
			return {
				icon: <CircularProgress size={22} />,
				label: 'Running',
				description: 'Creating report.',
				showProgress: true,
			};
		case 'completed':
			return {
				icon: <CheckCircleOutline color='success' />,
				label: 'Ready to download',
				description: job.output?.fileName ?? 'Your report is ready.',
				showProgress: false,
			};
		case 'noData':
			return {
				icon: <InfoOutlined color='info' />,
				label: 'No data found',
				description: 'No records matched the selected filters.',
				showProgress: false,
			};
		case 'cancelled':
			return {
				icon: <InfoOutlined color='action' />,
				label: 'Cancelled',
				description: 'Report request was cancelled.',
				showProgress: false,
			};
		default:
			return {
				icon: <ErrorOutline color='error' />,
				label: 'Report failed',
				description: job.error?.message ?? 'Report generation failed.',
				showProgress: false,
			};
	}
};

export const ReportStatusPanel = ({ job, reportTitle, isCancellingReport, onCancel, onDismiss, onDownload }: Props) => {
	const [isDownloading, setIsDownloading] = useState(false);
	const content = getPanelContent(job);
	const isClosed = isClosedJobStatus(job.status);

	const download = () => {
		if (job.status !== 'completed' || !job.output || isDownloading) {
			return;
		}

		setIsDownloading(true);

		try {
			onDownload(job);
		} finally {
			setIsDownloading(false);
		}
	};

	return (
		<Snackbar
			open
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			sx={{ maxWidth: 420, width: 'calc(100% - 32px)' }}
		>
			<Card
				aria-live='polite'
				elevation={8}
				sx={{
					borderLeft: 4,
					borderColor: `${job.status === 'failed' ? 'error' : 'primary'}.main`,
					p: 2,
					width: '100%',
				}}
			>
				<Stack spacing={1.5}>
					<Stack direction='row' alignItems='flex-start' spacing={1.5}>
						{content.icon}

						<Stack flex={1} spacing={0.25}>
							<Typography variant='subtitle2'>{reportTitle}</Typography>
							<Typography variant='body2' fontWeight={600}>
								{content.label}
							</Typography>
							<Typography variant='body2' color='text.secondary'>
								{content.description}
							</Typography>
							<Typography variant='caption' color='text.secondary'>
								Job {job.jobId.slice(0, 8)}
							</Typography>
						</Stack>

						{isClosed && (
							<IconButton aria-label='Dismiss report status' size='small' onClick={onDismiss}>
								<Close fontSize='small' />
							</IconButton>
						)}
					</Stack>

					{content.showProgress && <LinearProgress />}

					{!isClosed && (
						<Stack direction='row' justifyContent='flex-end'>
							<Button data-cy='cancel-report-button' onClick={onCancel} disabled={isCancellingReport}>
								{isCancellingReport ? 'Cancelling…' : 'Cancel Report'}
							</Button>
						</Stack>
					)}

					{job.status === 'completed' && (
						<Stack direction='row' justifyContent='flex-end'>
							<Button onClick={download} disabled={isDownloading}>
								{isDownloading ? 'Downloading…' : 'Download Report'}
							</Button>
						</Stack>
					)}
				</Stack>
			</Card>
		</Snackbar>
	);
};
